import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { AuthStateFacade, StoreErrorHandler } from '@zero/shared/util-global-state';
import { catchError, concatMap, filter, map, of, switchMap, tap } from 'rxjs';
import { TicketInfrastructure } from '../infrastructure/ticket.infrastructure';
import { TicketsApiActions, TicketsPageActions } from './ticket.actions';

import { ticketsFeature } from './ticket.reducer';

@Injectable()
export class TicketEffects {
  //TODO: remove with V5
  public loadTicketsDeprecated$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TicketsPageActions.loadTicketsDeprecated),
      concatLatestFrom(() => this.store.select(ticketsFeature.selectTicketsInitiallyLoaded)),
      filter(([action, loaded]) => !loaded || action.force),
      filter(() => Boolean(this.authStateFacade.customerId)),
      tap(() => TicketsPageActions.setTicketLoading({ ticketsLoading: true })),
      switchMap(() =>
        this.ticketInfrastructure.getTicketsDeprecated().pipe(
          map((tickets) => TicketsApiActions.loadTicketsDeprecatedSuccess({ tickets })),
          catchError((error: HttpErrorResponse) => of(TicketsApiActions.loadTicketsDeprecatedFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(TicketsApiActions.loadTicketsDeprecatedFailure({ error }))),
    );
  });

  public loadTickets$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TicketsPageActions.loadTickets),
      concatLatestFrom(() => this.store.select(ticketsFeature.selectTicketsInitiallyLoaded)),
      filter(([action, loaded]) => !loaded || action.force),
      filter(() => Boolean(this.authStateFacade.customerId)),
      tap(() => TicketsPageActions.setTicketLoading({ ticketsLoading: true })),
      concatMap(([{ page, size, status }]) =>
        this.ticketInfrastructure.getTickets(page, size, status).pipe(
          map((response) => {
            if (!response?.list?.length) {
              return TicketsApiActions.loadTicketsSuccess({ tickets: [], ticketsHasNext: false });
            }

            return TicketsApiActions.loadTicketsSuccess({ tickets: response.list, ticketsHasNext: response.hasNext });
          }),
          catchError((error: HttpErrorResponse) => of(TicketsApiActions.loadTicketsFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(TicketsApiActions.loadTicketsFailure({ error }))),
    );
  });

  public loadTicketsUnreadCount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TicketsPageActions.loadUnreadTicketsCount),
      concatLatestFrom(() => this.store.select(ticketsFeature.selectUnreadTicketsCountInitiallyLoaded)),
      filter(([action, loaded]) => !loaded || action.force),
      filter(() => Boolean(this.authStateFacade.customerId)),
      tap(() => TicketsPageActions.setTicketLoading({ ticketsLoading: true })),
      switchMap(() =>
        this.ticketInfrastructure.getUnreadTickets().pipe(
          map((unreadTicketsCount) => TicketsApiActions.loadUnreadTicketsCountSuccess({ unreadTicketsCount })),
          catchError((error: HttpErrorResponse) => of(TicketsApiActions.loadUnreadTicketsCountFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(TicketsApiActions.loadUnreadTicketsCountFailure({ error }))),
    );
  });

  public handleTicketBackendError$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(TicketsApiActions.loadTicketsFailure, TicketsApiActions.loadUnreadTicketsCountFailure),
        tap((action) => this.storeErrorHandler.handleBackendError(action.error)),
      );
    },
    { dispatch: false },
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly ticketInfrastructure: TicketInfrastructure,
    private readonly authStateFacade: AuthStateFacade,
    private readonly storeErrorHandler: StoreErrorHandler,
  ) {}
}
